import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, IconButton, InputAdornment, Pagination, Paper, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Refresh as RecheckIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { useScrollToListTop } from '../../hooks/useScrollToListTop';
import { darkTableSx, selectedChipProps } from './ui';
import { stagingApiBase, stagingOrg } from './stagingOrg';
import { VAPERITE_SELL_IN_APP_TAG } from './sellInAppTag';

// Live products still missing a value (Jason, 25 Oct 2026: a product may only be live
// without a description). "Hidden in app" = a Missing Value Rule hides it (no image /
// price / tags) until the gap is filled; it comes back by itself once fixed. Products
// live without a description are listed too, so that gap stays visible after approval.

// Brand (29 Oct 2026): listed when Vend has no brand for the product, never hidden for it.
type FieldKey = 'description' | 'image' | 'price' | 'tags' | 'brand';

const FIELDS: FieldKey[] = ['description', 'image', 'price', 'tags', 'brand'];
const LABELS: Record<FieldKey, string> = {
  description: 'Description', image: 'Image', price: 'Price', tags: 'Tags', brand: 'Brand',
};
const PAGE_SIZE = 50;

interface LiveMissingItem {
  id: string;
  sku: string;
  name: string;
  org: string;
  missing: FieldKey[];
  hidden: boolean;
  sellInApp: boolean;
  /** Vaperite: live without the Sell in app tag in Vend (listed, never hidden for it). */
  untagged?: boolean;
}

type Counts = Record<FieldKey | 'hidden' | 'untagged', number>;

const plural = (n: number) => `${n} product${n === 1 ? '' : 's'}`;

export default function LiveMissingTab({ onChanged }: { onChanged?: () => void } = {}) {
  const { colors } = useAdminTheme();
  const notify = useNotify();
  const navigate = useNavigate();
  const [items, setItems] = useState<LiveMissingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState<FieldKey[]>([]);
  const [hiddenOnly, setHiddenOnly] = useState(false);
  const [untaggedOnly, setUntaggedOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const listTopRef = useScrollToListTop(page);
  const [rechecking, setRechecking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/live-missing`, {
        params: {
          missing: missing.length ? FIELDS.filter((f) => missing.includes(f)).join(',') : undefined,
          hidden: hiddenOnly ? 'true' : undefined,
          untagged: untaggedOnly ? 'true' : undefined,
          search: search || undefined,
          page,
          limit: PAGE_SIZE,
        },
      });
      setItems(data.items || []);
      setTotal(data.total ?? 0);
      setCounts(data.counts ?? null);
    } catch (err: any) {
      notify(`Failed to load live products: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [missing, hiddenOnly, untaggedOnly, search, page, notify]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleField = (f: FieldKey) => {
    setMissing((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
    setPage(1);
  };

  const recheck = async () => {
    setRechecking(true);
    try {
      const { data } = await authInstance.post(`${stagingApiBase()}/staging/live-missing/recheck`);
      notify(
        data.hidden || data.unhidden
          ? `Re-checked: ${plural(data.hidden)} hidden, ${plural(data.unhidden)} back in the app`
          : 'Re-checked: nothing changed',
        { type: 'success' },
      );
      load();
      onChanged?.();
    } catch (err: any) {
      notify(`Re-check failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setRechecking(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Paper ref={listTopRef} sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
              Live: still missing
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              Products in the app that lack a value. A blocking gap (no image or price) hides a product until it is fixed;
              a missing description, tags or brand is allowed. Fix it in Products and it comes back by itself.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={<RecheckIcon />} onClick={recheck} disabled={rechecking}>
            {rechecking ? 'Re-checking…' : 'Re-check now'}
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mt: 2 }}>
          <TextField
            size="small"
            placeholder="Search SKU or name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Hidden in app (${counts?.hidden ?? 0})`}
              onClick={() => { setHiddenOnly((h) => !h); setPage(1); }}
              color="error"
              variant={hiddenOnly ? 'filled' : 'outlined'}
              {...selectedChipProps(hiddenOnly)}
              size="small"
            />
            {stagingOrg() === 'vaperite' && (
              <Chip
                label={`No ${VAPERITE_SELL_IN_APP_TAG} tag (${counts?.untagged ?? 0})`}
                title={`Live without the ${VAPERITE_SELL_IN_APP_TAG} tag in Vend: kept live as approved until Vaperite tags it`}
                onClick={() => { setUntaggedOnly((v) => !v); setPage(1); }}
                color={untaggedOnly ? 'warning' : 'default'}
                variant={untaggedOnly ? 'filled' : 'outlined'}
                {...selectedChipProps(untaggedOnly)}
                size="small"
              />
            )}
            {FIELDS.map((f) => (
              <Chip
                key={f}
                label={`${LABELS[f]} (${counts?.[f] ?? 0})`}
                onClick={() => toggleField(f)}
                color={missing.includes(f) ? 'warning' : 'default'}
                variant={missing.includes(f) ? 'filled' : 'outlined'}
                {...selectedChipProps(missing.includes(f))}
                size="small"
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ p: 3 }}>
          {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={44} sx={{ bgcolor: colors.bgElevated, mb: 0.5 }} />)}
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            No live products{missing.length || hiddenOnly || untaggedOnly || search ? ' match these filters' : ' are missing a value'}.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={darkTableSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Missing</TableCell>
                <TableCell>In the app</TableCell>
                <TableCell align="right">Fix</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{row.sku}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {FIELDS.filter((f) => row.missing.includes(f)).map((f) => (
                        <Chip
                          key={f}
                          label={f === 'description' || f === 'brand' ? `No ${LABELS[f].toLowerCase()}` : LABELS[f]}
                          color={f === 'description' || f === 'brand' ? 'default' : 'warning'}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                      {row.untagged && (
                        <Chip label={`No ${VAPERITE_SELL_IN_APP_TAG} tag`} title="Kept live as approved until Vaperite tags it" color="default" variant="outlined" size="small" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {row.hidden
                      ? <Chip label="Hidden" color="error" size="small" />
                      : <Chip label="Live" color="success" variant="outlined" size="small" />}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open in Products to fix">
                      <IconButton size="small" aria-label={`Open ${row.sku}`} onClick={() => navigate(`/products?search=${encodeURIComponent(row.sku)}`)} sx={{ color: colors.info }}>
                        <OpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} size="small" />
        </Box>
      )}
    </Paper>
  );
}
